import has from 'lodash/has';
import React, { useState } from 'react';
import { Feature, SYSTEM_ISO_CODES } from '@loyalty/shared-common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Paper, Tab, Tabs, TextField, Theme } from '@mui/material';
import { RuleType } from '@loyalty/shared-common/rule-api-client';
import { colorPalette } from '@qualifio/ui-commons';
import { createStyles, makeStyles } from '@mui/styles';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import { faProjectDiagram } from '@fortawesome/pro-solid-svg-icons/faProjectDiagram';
import { faText } from '@fortawesome/pro-solid-svg-icons/faText';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { BurningRuleFormValue } from './form';
import { BurningRulePanelCriteria } from './BurningRulePanelCriteria';
import { BurningRulePanelRewards } from './BurningRulePanelRewards';
import { RulePanelDescription } from '../rules/components';
import { rewardsIcon } from '../shared/icons';
import { useFeatures } from '../features/FeaturesContext';

interface StyleProps {
  tab: string;
}

const styles = ({ spacing }: Theme) => {
  return createStyles<string, StyleProps>({
    panel: {
      border: `1px solid ${colorPalette.grey.main}`,
    },
    panelContent: {
      padding: spacing(2),
    },
    panelDescription: {},
    tabs: {
      flex: 1,
    },
    actions: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
    },
    errorIcon: {
      marginLeft: spacing(0.5),
    },
  });
};

const useStyles = makeStyles(styles);

enum TabEnum {
  Rewards = 'EARNING',
  Criterias = 'CRITERIAS',
  Description = 'DESCRIPTION',
}

const descriptionKeys = SYSTEM_ISO_CODES.map(isoCode => `i18nAttributes.${isoCode}.description`);

export function BurningRulePanels(): React.ReactElement {
  const { t } = useTranslation();
  const [tab, setTab] = useState(TabEnum.Rewards);
  const { features } = useFeatures();
  const { write: burningRuleWrite } = features[Feature.BURNING_RULE];
  const classes = useStyles({ tab });
  const {
    register,
    formState: { errors },
  } = useFormContext<BurningRuleFormValue>();

  const isTab = (name: string) => name === tab;

  const hasErrors = (...args: string[]) => args.filter(k => has(errors, k)).length > 0;

  return (
    <div>
      <TextField
        {...register('name')}
        fullWidth
        placeholder={t('earning-rules.form.fields.name')}
        sx={{ marginBottom: 2 }}
        error={!!errors?.name}
        helperText={errors?.name?.message}
      />
      <Paper className={classes.panel}>
        <div className={classes.actions}>
          <Tabs className={classes.tabs} value={tab} onChange={(_, value) => setTab(value)}>
            <Tab
              value={TabEnum.Rewards}
              label={
                <>
                  {t('burning-rules.tabs.earning')}
                  {hasErrors('actions.trans', 'actions.reward') && (
                    <FontAwesomeIcon
                      className={classes.errorIcon}
                      color="red"
                      icon={faExclamationCircle}
                    />
                  )}
                </>
              }
              icon={<FontAwesomeIcon icon={rewardsIcon} />}
            />
            <Tab
              value={TabEnum.Criterias}
              label={
                <>
                  {t('burning-rules.tabs.criteria')}
                  {hasErrors('criteria') && (
                    <FontAwesomeIcon
                      className={classes.errorIcon}
                      color="red"
                      icon={faExclamationCircle}
                    />
                  )}
                </>
              }
              id={'create-burning-rule-criteria-id'}
              icon={<FontAwesomeIcon icon={faProjectDiagram} />}
            />
            <Tab
              value={TabEnum.Description}
              label={
                <>
                  {t('burning-rules.tabs.description')}
                  {hasErrors(...descriptionKeys) && (
                    <FontAwesomeIcon
                      className={classes.errorIcon}
                      color="red"
                      icon={faExclamationCircle}
                    />
                  )}
                </>
              }
              id={'create-burning-rule-description-id'}
              icon={<FontAwesomeIcon icon={faText} />}
            />
          </Tabs>
        </div>
        <div className={classes.panelContent}>
          {isTab(TabEnum.Rewards) && <BurningRulePanelRewards />}
          {isTab(TabEnum.Criterias) && <BurningRulePanelCriteria />}
          {isTab(TabEnum.Description) && (
            <RulePanelDescription
              disabled={!burningRuleWrite}
              type={RuleType.BURN}
              className={classes.panelDescription}
            />
          )}
        </div>
      </Paper>
    </div>
  );
}
